import {englishLabel, FormatLabel, LabelDescriptor} from './projectStatusLabels';

/**
 * The project-stage vocabulary, deliberately in its own module beside `projectStatusLabels`.
 *
 * A project has BOTH a lifecycle Stage and a RAG Status, and until this was settled a reader met
 * two different concepts both called Status. DECIDED with product: Stage reads Opportunity /
 * Planning / Running / On Hold / Done, Status reads On Track / At Risk / Off Track / em dash. The
 * two files are separate so the distinction is visible in the imports, not just in a comment.
 */

/** The five values `projectStatus.stage` is published with, as `project_status.json` spells them
 * and in the order it lists them. */
const STAGE_WIRE_VALUES = ['OPPORTUNITY', 'PLANNING', 'RUNNING', 'HALTED', 'DONE'] as const;

/** Wire token -> message. Four are a plain title-casing, which is exactly why the fifth is worth a
 * map: `HALTED` is shown as On Hold, a word the wire never uses, so deriving these with a
 * toLowerCase/capitalise would be right four times and wrong on the one that matters.
 *
 * Four of the five REUSE the app's existing stage vocabulary (`project_status.*`, the same keys
 * `Util.getProjectStageName` and the filter chips resolve these tokens through) rather than
 * declaring a parallel set. That is not only de-duplication: those keys are already translated into
 * every locale we ship, so a Danish reader sees `I gang` here on day one, where a fresh
 * `ai_report_builder.*` key would have shipped the English default until a translator got to it -
 * which would have defeated the point of threading a formatter down to the row mapper at all.
 * `HALTED` is the exception and keeps its own key: the existing `project_status.halted` reads
 * `Halted`, and DECIDED with product is `On Hold`.
 *
 * A `Map` rather than an object literal because the key is a raw wire string: an object lookup
 * answers for `constructor`, `toString` and `__proto__` off the prototype chain, and formatting one
 * of those as a descriptor yields `undefined` where the type promises a string. */
const STAGE_MESSAGE = new Map<string, LabelDescriptor>([
	['OPPORTUNITY', {id: 'project_status.opportunity', defaultMessage: 'Opportunity'}],
	['PLANNING', {id: 'project_status.planning', defaultMessage: 'Planning'}],
	['RUNNING', {id: 'project_status.running', defaultMessage: 'Running'}],
	['HALTED', {id: 'ai_report_builder.project_stage.on_hold', defaultMessage: 'On Hold'}],
	['DONE', {id: 'project_status.done', defaultMessage: 'Done'}],
]);

/**
 * What a reader is shown for a raw `projectStatus.stage` value.
 *
 * An unrecognised token is echoed rather than replaced: unlike an unusable RAG value - which must
 * never be presented as a state - a stage this client has not heard of still names a real project
 * state, and showing the token tells the reader more than hiding it. It is echoed verbatim rather
 * than translated, because there is no message to translate it with. A missing stage answers with
 * an empty string, which is this function saying it has no label rather than a label that happens to
 * be blank - the caller decides how that reads, and the row mapper shows it as `N/A`.
 */
export function stageLabelFor(wireValue: unknown, formatLabel: FormatLabel = englishLabel): string {
	if (typeof wireValue !== 'string' || wireValue === '') {
		return '';
	}
	const message = STAGE_MESSAGE.get(wireValue);
	return message ? formatLabel(message) : wireValue;
}

/** The five stages as a picker's candidates: the wire token as the id a condition stores, the
 * display label beside it. */
export function projectStageCandidates(formatLabel: FormatLabel = englishLabel): {id: string; label: string}[] {
	return STAGE_WIRE_VALUES.map(value => ({id: value, label: stageLabelFor(value, formatLabel)}));
}
